import { ref, readonly } from 'vue';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { initializeCometChatBridge } from '@/utils/cometChatBridge';


const instance = ref(null);
const isInitialized = ref(false);
const isInitializing = ref(false);
const initializationError = ref(null);
let initializationPromise = null;

export function useCometChat() {
    async function init(appId, appSettings) {
        if (isInitialized.value && instance.value) {
            return instance.value;
        }

        if (initializationPromise) {
            return initializationPromise;
        }

        isInitializing.value = true;
        initializationError.value = null;

        initializationPromise = (async () => {
            try {
                await CometChat.init(appId, appSettings);

                instance.value = CometChat;
                isInitialized.value = true;

                initializeCometChatBridge(CometChat);

                return CometChat;
            } catch (error) {
                initializationError.value = error;
                isInitialized.value = false;
                instance.value = null;
                throw error;
            } finally {
                isInitializing.value = false;
                initializationPromise = null;
            }
        })();

        return initializationPromise;
    }

    async function getInstance() {
        if (instance.value) {
            return instance.value;
        }

        if (initializationPromise) {
            return await initializationPromise;
        }

        throw new Error('[useCometChat] SDK not initialized. Call init() first.');
    }

    function getInstanceSync() {
        return instance.value;
    }

    function isReady() {
        return isInitialized.value && instance.value !== null;
    }

    return {
        init,
        getInstance,
        getInstanceSync,
        isReady,
        isInitialized: readonly(isInitialized),
        isInitializing: readonly(isInitializing),
        initializationError: readonly(initializationError)
    };
}
